import { AxiosError } from "axios";
import { formatISO } from "date-fns";
import { type FC, Fragment } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { toastSuccessMessage } from "../../utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "../../utils/amplitude";

import { ErrorMessageBanner } from "./ErrorMessageBanner";
import { LabeledCheckbox } from "./LabeledCheckbox";
import { SubmitOrCancelButtons } from "./SubmitOrCancelButtons";

import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { LinkButton } from "mds/components/LinkButton";
import { CircleSuccessGreenIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectDesignatedSection, selectSectionScorePageCount } from "store/selectors";

type FormValues = {
  section_score_enabled: boolean;
  section_score_prepwork_percentage: number;
  section_score_activities_percentage: number;
  section_score_assignments_percentage: number;
};

interface SectionSettingsGradeFormProps {
  className?: string;
  onClose: () => void;
}

export const SectionSettingsGradeForm: FC<SectionSettingsGradeFormProps> = ({
  className,
  onClose,
}) => {
  const currentSection = useAppSelector(selectDesignatedSection);

  const pageCount = useAppSelector((s) => selectSectionScorePageCount(s, currentSection?.id));

  const form = useForm<FormValues>({
    defaultValues: {
      section_score_enabled: currentSection?.section_score_enabled,
      section_score_prepwork_percentage: currentSection?.section_score_prepwork_percentage,
      section_score_activities_percentage: currentSection?.section_score_activities_percentage,
      section_score_assignments_percentage: currentSection?.section_score_assignments_percentage,
    },
  });

  const enabled = form.watch("section_score_enabled");

  if (!currentSection) {
    return null;
  }

  const onSubmit: SubmitHandler<FormValues> = (formData) => {
    trackEvent("Section Settings - Save changes", BUTTON_EVENT_CATEGORY);
    return storeApi.sections
      .partial_update({ id: currentSection.id, ...formData }, { skipToast: true })
      .then(() => {
        toastSuccessMessage(t("success.toasts.updated_section_settings"));
        onClose();
      })
      .catch((error: unknown) => {
        if (error instanceof AxiosError) {
          const validationError = error.response?.data as Record<string, string[]>;
          if (validationError && validationError.section_score_percentages) {
            form.setError("root.section_score_percentages", {
              type: "400",
              message: t("settings.section_score_percentages_must_sum_to_100"),
            });
          }
        }
      });
  };

  const toggleSectionScoresPublished = () =>
    storeApi.sections.partial_update({
      id: currentSection.id,
      section_score_published_at: currentSection.section_score_published_at
        ? null
        : formatISO(new Date()),
    });

  return (
    <FormProvider {...form}>
      <form
        className={twMerge(
          "flex flex-col gap-6 text-sm font-medium leading-[1.3125rem] text-black-tint-10",
          className,
        )}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <LabeledCheckbox
          eventName="Section Settings - Course score toggle"
          eventProps={{ sectionTitle: currentSection.title }}
          registration={form.register("section_score_enabled")}
        >
          {t("settings.section.section_score_enabled")}
        </LabeledCheckbox>

        <fieldset className="flex flex-col gap-2">
          <p>{t("settings.section_score_description")}</p>
          {(["prepwork", "activities", "assignments"] as const).map((category) => (
            <Fragment key={category}>
              <label className="flex flex-row items-center justify-between">
                <div>{t(`settings.section.section_score_${category}_percentage`)}</div>
                <div
                  className="mds-text-field-icon data-[disabled]:cursor-not-allowed data-[disabled]:bg-black-tint-90 data-[disabled]:text-black-tint-55"
                  data-disabled={!enabled || undefined}
                >
                  <input
                    className="mds-text-field mds-text-field-number text-black-tint-20 disabled:cursor-not-allowed"
                    {...form.register(`section_score_${category}_percentage`, {
                      disabled: !enabled,
                      setValueAs: (value: string): number => {
                        if (value === "") {
                          return 0;
                        }
                        return +value;
                      },
                      validate: (value: number) => {
                        if (!Number.isInteger(value) || value < 0) {
                          return t("error.whole_number_required");
                        }
                      },
                    })}
                  />
                  <span>%</span>
                </div>
              </label>
              <ErrorMessageBanner name={`section_score_${category}_percentage`} />
            </Fragment>
          ))}
          <ErrorMessageBanner name="root.section_score_percentages" />
        </fieldset>

        <div className="flex flex-col gap-2 text-black-tint-20">
          {currentSection.section_score_published_at ? (
            <IconText
              iconStart={<CircleSuccessGreenIcon />}
              text={t("settings.section_scores_published")}
            />
          ) : (
            <div>{t("settings.section_scores_unpublished")}</div>
          )}

          <div className="text-sm font-normal">
            {currentSection.section_score_published_at
              ? t("settings.section_scores_description_published")
              : t("settings.section_scores_description_unpublished")}
          </div>

          <div className="text-sm font-normal">
            {t("settings.section_scores_assessment_count", { count: pageCount })}
          </div>

          <LinkButton
            className={twMerge(
              "h-[32px] disabled:text-black-tint-40",
              currentSection.section_score_published_at
                ? "text-red hover:text-red"
                : "text-blue hover:text-blue",
            )}
            disabled={!currentSection.section_score_enabled}
            eventName={
              currentSection.section_score_published_at
                ? "Section Settings - Unpublish Course Scores"
                : "Section Settings - Publish Course Scores"
            }
            onClick={toggleSectionScoresPublished}
          >
            {currentSection.section_score_published_at
              ? t("settings.unpublish_section_scores")
              : t("settings.publish_section_scores")}
          </LinkButton>
        </div>

        <SubmitOrCancelButtons className="pt-8" onCancel={onClose} />
      </form>
    </FormProvider>
  );
};
